import { registerUser, validateUserData } from 'api/user';
import { Anchor, Button } from 'components/atoms';
import SignInWithGoogle from 'components/atoms/SignInWithGoogle/SignInWithGoogle';
import { Head, PageLayout } from 'components/molecules';
import {
  NewsletterForm,
  RechtlichesForm,
} from 'components/user';
import ZugangsdatenForm from 'components/user/UserDataForm/ZugangsDatenForm';
import { getTranslations } from 'i18n';
import type { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import React, { useRef, useState } from 'react';
import { FormErrors } from 'types';
import { User } from 'types/user';
import { filterServerSideErrors, setEmptyValuesNull } from 'utils';
import { gaEventRemoveAboFromCart } from 'utils/events/google';
import { useStateCallback, useToastNotifications, useTranslation } from 'utils/hooks';
import { UserFormSectionRef } from 'utils/hooks/useUserForm';
import PRODUCTS from 'utils/products';



interface RegisterPageProps {}

const RegisterPage: NextPage<RegisterPageProps> = () => 
{
  const { t } = useTranslation('user');
  const router = useRouter();

  const [registration, setRegistration] = useState<Partial<User>>({});
  const reviewMode = false;
  const [serverSideErrors, setServerSideErrors] = useStateCallback<FormErrors>({});

  const [, addToastNotification] = useToastNotifications();
  const { status } = useSession();

  // Refs
  const zugangsDatenRef = useRef<UserFormSectionRef | null>(null);
  const newsletterRef = useRef<UserFormSectionRef | null>(null);
  const rechtlichesRef = useRef<UserFormSectionRef | null>(null);

  // Functions
  async function onSubmit(data: any) 
  {
    setRegistration((current) => ({ ...current, ...data }));
    return true;
  }

  async function validateServerSide(data: any): Promise<boolean>
  {
    const res = await validateUserData(setEmptyValuesNull(data));

    if (res.success) 
    {
      return true;
    }

    if (!res.success && res.errors) 
    {
      setServerSideErrors(res.errors);
      return false;
    }

    addToastNotification(t('forms:errors.unknown'));
    return false;
  }

  async function validateClientSide(): Promise<null | any> 
  {
    const refs = [zugangsDatenRef, newsletterRef, rechtlichesRef];

    const sections = await Promise.all(refs.map((ref) => ref.current?.submit()));

    return sections.every((section) => section !== null)
      ? sections.reduce((combined, current) => ({ ...combined, ...current }), {})
      : null;
  }

  const saveUser = async () => 
  {
    const client = await validateClientSide();
    if (!client) return;

    const server = await validateServerSide(client);
    if (!server) return;

    const res = await registerUser(setEmptyValuesNull(client));

    if (res.success && res.url) 
    {
      router.push(res.url);
      return;
    }

    addToastNotification(t('forms:errors.unknown'));
  };

  if (status === 'authenticated')
  {
    router.replace('/user');
    return null;
  }

  return (
    <PageLayout textContentPage>

      <Head title={t('user:register.steps.0')}>
        <meta name="robots" content="noindex"/>
      </Head>


      <SignInWithGoogle />
      <ZugangsdatenForm
        ref={zugangsDatenRef}
        onFormSubmit={onSubmit}
        reviewMode={reviewMode}
        initialValues={registration?.zugangsDaten}
        serverSideErrors={filterServerSideErrors(serverSideErrors, 'zugangsDaten')}
      />

      <hr/>

      <NewsletterForm
        ref={newsletterRef}
        onFormSubmit={onSubmit}
        reviewMode={reviewMode}
        initialValues={registration?.newsletter}
        serverSideErrors={filterServerSideErrors(serverSideErrors, 'newsletter')}
      />

      <hr/>

      <RechtlichesForm
        ref={rechtlichesRef}
        onFormSubmit={onSubmit}
        reviewMode={reviewMode}
        initialValues={registration?.rechtliches}
        serverSideErrors={filterServerSideErrors(serverSideErrors, '')}
      />

      <hr/>

      <div className='row space'>
        <Anchor
          href="/produkte"
          onClick={() => gaEventRemoveAboFromCart(PRODUCTS[registration!.abo!], registration?.unternehmensDaten?.istPrivatKonto)}
        >
          {t('user:register.backToSelection')}
        </Anchor>
        <Button onClick={() => setServerSideErrors({}, saveUser)}>
          {t('user:register.registerNow')}
        </Button>
      </div>

    </PageLayout>
  );
};

export default RegisterPage;

export const getServerSideProps: GetServerSideProps = async (ctx) => 
{
  const { locale } = ctx;
  const translations = await getTranslations(locale, 'user', 'products');

  return { props: { ...translations } };
};
